import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { FloatingAlert } from "@/components/ui/floating-alert";
import { useUser } from "@/contexts/UserContext";
import { apiRequest } from "@/lib/api-request";
import { colors } from "@/design-system/colors";
import { MyAiUsageSection } from "../usage/MyAiUsageSection";
import { TeamAiUsageSection } from "../usage/TeamAiUsageSection";
import { BobReviewScheduleSection } from "../usage/BobReviewScheduleSection";

/**
 * Settings > Usage -- communicates AI USAGE, never AI COST. Only ever
 * renders a percentage, a plan name, and a reset date -- never credits,
 * tokens, or a dollar figure (those stay backend/DB-only, per the
 * ai_usage/credit_accounts/ai_plans system in backend/core/ai/credits.js
 * and backend/migrations/phase36_ai_user_monthly_budget.sql).
 *
 * Reads Supabase directly (RLS-scoped to the caller's own row/company),
 * same pattern the rest of Settings already uses -- no new REST endpoint
 * needed, credit_accounts_read/ai_plans_authenticated_read already allow
 * a JWT client to read exactly this.
 */

interface UsageState {
  percentUsed: number;
  planName: string;
  nextResetLabel: string;
  status: "ok" | "warning" | "critical" | "limit";
}

function nextResetDate(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

function currentPeriodKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

function statusForPercent(pct: number): UsageState["status"] {
  if (pct >= 100) return "limit";
  if (pct >= 95) return "critical";
  if (pct >= 80) return "warning";
  return "ok";
}

function PlanUsageCard() {
  const { user, companyId } = useUser();
  const [usage, setUsage] = useState<UsageState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissedAlert, setDismissedAlert] = useState(false);

  useEffect(() => {
    if (!user?.id || !companyId) return;

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        // The plan, the user's own share of the company budget and what is left of it are computed by the backend (GET /api/ai-usage/credits)
        const credits = await apiRequest<{ plan_name: string | null; plan_label?: string | null; total_credits: number; available_credits: number }>("api/ai-usage/credits");

        const totalCredits = Number(credits.total_credits) || 0;
        const availableCredits = Number(credits.available_credits) || 0;
        const usedCredits = Math.max(0, totalCredits - availableCredits);
        const percentUsed = totalCredits > 0 ? Math.min(100, Math.round((usedCredits / totalCredits) * 100)) : 0;

        if (!cancelled) {
          setUsage({
            percentUsed,
            // the backend names the plan for people (plan_label: "Pro"); the technical key (plan_name) is never shown
            planName: `${credits.plan_label || "Pro"} plan`,
            // timeZone: 'UTC' is required here -- the reset boundary itself
            // is a UTC calendar month (ai_current_budget_period()), so
            // formatting in the viewer's LOCAL timezone would display the
            // wrong calendar date for anyone west of UTC (midnight UTC
            // Oct 1 renders as "Sep 30" in most US timezones otherwise).
            nextResetLabel: nextResetDate().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }),
            status: statusForPercent(percentUsed),
          });
        }
      } catch (error) {
        console.error("[Usage] Failed to load AI usage:", error);
        if (!cancelled) setUsage(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, companyId]);

  // Show the 80%/95%/100% floating alert at most once per threshold per
  // calendar period (localStorage-backed) -- never re-fires on every
  // render/poll/tab-revisit within the same month.
  const alertToShow = useMemo(() => {
    if (!usage || usage.status === "ok" || dismissedAlert || !user?.id) return null;

    const storageKey = `krewup:ai-usage-alert:${user.id}:${currentPeriodKey()}:${usage.status}`;
    try {
      if (localStorage.getItem(storageKey)) return null;
    } catch {
      // localStorage unavailable (private mode, etc.) -- fine to just show it once per session.
    }

    if (usage.status === "limit") {
      return { variant: "destructive" as const, title: "AI usage limit reached", description: `You've reached your ${usage.planName} AI usage limit for this period. It resets on ${usage.nextResetLabel}.`, storageKey };
    }
    if (usage.status === "critical") {
      return { variant: "destructive" as const, title: "AI usage at 95%", description: `You're close to your ${usage.planName} AI usage limit. It resets on ${usage.nextResetLabel}.`, storageKey };
    }
    return { variant: "warning" as const, title: "AI usage at 80%", description: `You've used 80% of your ${usage.planName} AI usage for this period.`, storageKey };
  }, [usage, dismissedAlert, user?.id]);

  const handleDismissAlert = () => {
    if (alertToShow) {
      try {
        localStorage.setItem(alertToShow.storageKey, "1");
      } catch {
        // ignore
      }
    }
    setDismissedAlert(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-gray-500 font-medium">Loading usage...</p>
      </div>
    );
  }

  if (!usage) {
    return (
      <Card className="max-w-2xl">
        <CardContent className="p-6">
          <p className="text-sm text-gray-500">AI usage isn't available right now. Try again later.</p>
        </CardContent>
      </Card>
    );
  }

  const statusCopy: Record<UsageState["status"], { label: string; className: string }> = {
    ok: { label: "Normal", className: "text-green-700 bg-green-50" },
    warning: { label: "Approaching limit", className: "text-yellow-700 bg-yellow-50" },
    critical: { label: "Near limit", className: "text-red-700 bg-red-50" },
    limit: { label: "Limit reached", className: "text-red-700 bg-red-50" },
  };

  return (
    <>
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="font-heading-text">AI Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{usage.percentUsed}% used</span>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusCopy[usage.status].className}`}>
                {statusCopy[usage.status].label}
              </span>
            </div>
            <Progress
              value={usage.percentUsed}
              className="bg-gray-100"
              indicatorColor={
                usage.status === "ok"
                  ? colors.tagStatusActive
                  : usage.status === "warning"
                  ? colors.tagStatusPending
                  : colors.tagStatusRejected
              }
            />
            <p className="mt-2 text-sm text-gray-500">{100 - usage.percentUsed}% remaining this period</p>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500">Plan</p>
              <p className="text-sm font-medium text-gray-900">{usage.planName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Next reset</p>
              <p className="text-sm font-medium text-gray-900">{usage.nextResetLabel}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {alertToShow && (
        <FloatingAlert
          variant={alertToShow.variant}
          title={alertToShow.title}
          description={alertToShow.description}
          onDismiss={handleDismissAlert}
        />
      )}
    </>
  );
}

/**
 * Settings > Usage: the plan's credit usage (above), the signed-in user's own AI consumption and budget, the team's consumption and budgets
 * (company admins and members) and Bob's daily-review schedule. Each part loads on its own, so one failing never hides the others.
 */
export function UsageTab() {
  const { userRole } = useUser();
  return (
    <div className="space-y-6">
      <PlanUsageCard />
      <MyAiUsageSection />
      {(userRole === "admin" || userRole === "member") && <TeamAiUsageSection />}
      <BobReviewScheduleSection />
    </div>
  );
}
